import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { auth, firebaseEnabled } from '../lib/firebase';
import type { User } from 'firebase/auth';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import SignInModal from '../components/SignInModal';

export type AuthContextValue = {
  isAuthenticated: boolean;
  currentUser: User | null;
  openSignInModal: () => void;
  closeSignInModal: () => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>((auth && (auth as any).currentUser) || null);
  const [showSignInModal, setShowSignInModal] = useState(false);

  useEffect(() => {
    if (!firebaseEnabled || !auth) return undefined;
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsub();
  }, []);

  const openSignInModal = () => setShowSignInModal(true);
  const closeSignInModal = () => setShowSignInModal(false);

  const signOut = async () => {
    if (!firebaseEnabled || !auth) return Promise.resolve();
    try {
      await firebaseSignOut(auth);
    } catch (e) {
      console.error('Error signing out', e);
    }
  };

  const value = useMemo(() => ({
    isAuthenticated: !!currentUser,
    currentUser,
    openSignInModal,
    closeSignInModal,
    signOut,
  }), [currentUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SignInModal show={showSignInModal} onClose={() => setShowSignInModal(false)} />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
